import { isLeaf, type SlateBlock, type SlateLeaf } from '@atlas/renderer';
import escapeHtml from 'escape-html';

const collectAttributes = (leaf: SlateLeaf, options: TSerializeOptions): Record<string, string | number> => {
    const attrs: Record<string, string | number> = {};

    if (leaf.link) {
        attrs.href = leaf.link;
        attrs.target = '_blank';
    }

    if (leaf.constant) {
        const ops = leaf.constant.split(':');
        if (ops.length >= 3) {
            if (ops.at(-1) === 'mention') {
                attrs['data-mentioned'] = ops.at(-2) ?? ''; // Is a string because we checked the overall length above
            }
            if (ops.at(-1) === 'article') {
                attrs.href = `${options.helpCenterUrl}/articles/${ops.at(-2)}`;
                attrs['data-article'] = ops.at(-2) ?? ''; // Is a string because we checked the overall length above
            }
        }
    }

    if (leaf.data) {
        Object.entries(leaf.data).forEach(([key, value]) => {
            attrs[`data-${key}`] = value;
        });
    }

    return attrs;
};

const serialize = (node: SlateBlock | SlateLeaf, options: TSerializeOptions): string => {
    // TODO: @Stan add support for image, video, video-embed & table

    if (isLeaf(node)) {
        const attrs = collectAttributes(node, options);
        let string = escapeHtml(node.text);

        if (node.bold) {
            string = `<strong>${string}</strong>`;
        }

        if (node.code) {
            string = `<code>${string}</code>`;
        }

        if (node.italic) {
            string = `<em>${string}</em>`;
        }

        if (node.underline) {
            string = `<u>${string}</u>`;
        }

        if (node.strikethrough) {
            string = `<s>${string}</s>`;
        }

        if (node.constant) {
            string = `<span data-constant="${node.constant}">${string}</span>`;
        }

        const attrsStr = Object.keys(attrs).length
            ? Object.keys(attrs)
                  .map(
                      (name) =>
                          `${name}="${
                              typeof attrs[name] === 'undefined' || attrs[name] === null
                                  ? ''
                                  : escapeHtml(attrs[name] as string)
                          }"`,
                  )
                  .join(' ')
            : '';

        const tagName = attrs.href ? 'a' : 'span';

        return attrsStr ? `<${tagName} ${attrsStr}>${string}</${tagName}>` : string;
    }

    switch (node.type) {
        case 'image': {
            // safe to remove after we store messages AST in DB and render per channel on server
            const classNameForEmail = 'atl-inline-img-link';

            const alt = node.alt?.replace(/"/g, '&quot;') || node?.fileName || '';
            const widthAttr = node.width ? `width="${node.width}"` : null;
            return `<a href="${
                node.link || node.src
            }" target="_blank" title="${alt}" class="${classNameForEmail}"><img loading="lazy" src="${
                node.src
            }" alt="${alt}" ${widthAttr} /></a>`;
        }
        case 'embed': {
            return node.source;
        }
        case 'horizontal-line':
            return '<hr />';
    }

    let children = node.children.map((n) => serialize(n, options)).join('');

    if (!children) {
        return '';
    }

    if (node.type !== 'code-block') {
        children = children.replace(/\n/g, '<br/>');
    }

    switch (node.type) {
        case 'block-quote':
            return `<blockquote><p>${children}</p></blockquote>`;
        case 'code-block':
            return `<pre>${children}</pre>`;
        case 'heading-one':
            return `<h1>${children}</h1>`;
        case 'heading-two':
            return `<h2>${children}</h2>`;
        case 'heading-three':
            return `<h3>${children}</h3>`;
        case 'numbered-list':
            return `<ol${node.start && node.start > 1 ? ` start="${node.start}"` : ''}>${children}</ol>`;
        case 'bulleted-list':
            return `<ul>${children}</ul>`;
        case 'list-item':
            return `<li>${children}</li>`;
        case 'paragraph':
            return `<p>${children}</p>`;
        default:
            return `<div>${children}</div>`;
    }
};

export default serialize;

type TSerializeOptions = { helpCenterUrl: string };

// #region Tests

if (import.meta.vitest) {
    const { describe, it, expect } = import.meta.vitest;
    describe('serialize', () => {
        it('serialize slate nodes to HTML', () => {
            const slateNode: SlateBlock = { type: 'paragraph', children: [{ text: 'content in paragraph' }] };

            expect(serialize(slateNode, { helpCenterUrl: '' })).toBe('<p>content in paragraph</p>');
        });
    });
}

// #endregion
